import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';

interface User {
  id: string;
  email: string;
  name?: string;
  roles?: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (redirectUrl?: string) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication status on mount
  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(API_ENDPOINTS.auth.me, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
      } else if (response.status === 401) {
        setUser(null);
        setIsAuthenticated(false);
      } else {
        throw new Error('Failed to check authentication status');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      // Don't show error for network failures - just treat as unauthenticated
      if (err instanceof TypeError && err.message.includes('fetch')) {
        console.warn('API not available, treating as unauthenticated');
      } else {
        setError(err instanceof Error ? err.message : 'Authentication check failed');
      }
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login function - redirects to Cloudflare Access
  const login = useCallback((redirectUrl?: string) => {
    const loginUrl = `${API_ENDPOINTS.auth.login}${redirectUrl ? `?redirect=${encodeURIComponent(redirectUrl)}` : ''}`;
    window.location.href = loginUrl;
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(API_ENDPOINTS.auth.logout, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(null);
        setIsAuthenticated(false);
        
        // Redirect to Cloudflare Access logout
        if (data.logoutUrl) {
          window.location.href = data.logoutUrl;
        } else {
          // Fallback to login page
          window.location.href = '/login';
        }
      } else {
        throw new Error('Logout failed');
      }
    } catch (err) {
      console.error('Logout failed:', err);
      setError(err instanceof Error ? err.message : 'Logout failed');
    }
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(API_ENDPOINTS.auth.refresh, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
      } else if (response.status === 401) {
        setUser(null);
        setIsAuthenticated(false);
      } else {
        throw new Error('Session refresh failed');
      }
    } catch (err) {
      console.error('Session refresh failed:', err);
      setError(err instanceof Error ? err.message : 'Session refresh failed');
    }
  }, []);

  // Set up automatic session refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    // Refresh session every 30 minutes
    const interval = setInterval(() => {
      refreshSession();
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshSession]);

  // Handle visibility change - refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        refreshSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, refreshSession]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}